import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import FormSection from '@Guidelines/Component/FormSection';
import type FieldDraft from '@UI/Pages/Subjects/Shared/FormData/FieldDraft';
import SheetField from '@UI/Pages/Subjects/Sidebars/CreateSubjectSidebar/SheetField';
import type SubjectSheetFormData from '@UI/Pages/Subjects/Shared/FormData/SubjectSheetFormData';
import * as React from 'react';
import { InputText } from '@verclam/ui-kit';

type Props = {
	readonly titleLabel: string;
	readonly sheet: SubjectSheetFormData;
	readonly onSheetChange: (update: (sheet: SubjectSheetFormData) => SubjectSheetFormData) => void;
	readonly autoFocusTitle?: boolean;
	/** Fields shown above the title in the "Dossier" section. */
	readonly children?: React.ReactNode;
};

/** The "Dossier" section (title of the dossier), then the fields of its sheet under "Fiche" when the type has any. */
const SheetFields: React.FC<Props> = ({
	titleLabel,
	sheet,
	onSheetChange,
	autoFocusTitle = false,
	children,
}) => {
	const handleTitleChange = React.useCallback(
		(value: string) => {
			onSheetChange((current) => current.setTitle(value));
		},
		[onSheetChange],
	);

	const handleValueChange = React.useCallback(
		(key: string, value: FieldDraft) => {
			onSheetChange((current) => current.setValue(key, value));
		},
		[onSheetChange],
	);

	const entries = React.useMemo(() => [...sheet.entries.values()], [sheet.entries]);

	return (
		<React.Fragment>
			<FormSection title="Dossier">
				{children}
				<InputText
					name="title"
					label={titleLabel}
					required
					value={sheet.title.title}
					onChange={handleTitleChange}
					errorMessage={sheet.titleErrorMessage}
					isFormFieldValid={sheet.isTitleShownValid}
					autoFocus={autoFocusTitle}
				/>
			</FormSection>
			<ConditionalLoader data={entries.length > 0}>
				<FormSection title="Fiche">
					{entries.map((entry) => (
						<SheetField
							key={entry.definition.key}
							entry={entry}
							onValueChange={handleValueChange}
						/>
					))}
				</FormSection>
			</ConditionalLoader>
		</React.Fragment>
	);
};

export default SheetFields;
